import React, { useState } from 'react';
import axios from 'axios';
import './SigningInterface.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';

function SigningInterface({ customer, truck, onSigningComplete, documentPath }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [saveAsDraft, setSaveAsDraft] = useState(false);
  const [signingResult, setSigningResult] = useState(null);

  const handleSendForSignature = async () => {
    if (!customer?.email) {
      setError('Customer email is required for signing');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    setSigningResult(null);

    try {
      console.log('Sending for signature...');
      console.log('Mode:', saveAsDraft ? 'DRAFT' : 'SEND');

      const response = await axios.post(`${API_URL}/api/send-for-signature`, {
        pdfPath: documentPath,
        documentType: 'all',
        signerEmail: customer.email,
        signerName: customer.contactName || customer.companyName,
        stockNumber: truck.stockNumber,
        sendMode: saveAsDraft ? 'draft' : 'send'
      });

      console.log('✓ Success:', response.data);
      setSigningResult(response.data);
      setSuccess(response.data.message);

      // Call the parent callback if provided
      if (onSigningComplete && response.data.mode !== 'draft') {
        onSigningComplete(response.data);
      }

    } catch (err) {
      console.error('Error sending for signature:', err);
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Link copied to clipboard!');
    });
  };

  return (
    <div className="signing-interface">
      <h2>📝 Send for Electronic Signature</h2>
      
      {/* Customer Info Display */}
      <div className="customer-info">
        <div className="info-row">
          <strong>Signer:</strong> {customer.contactName || customer.companyName}
        </div>
        <div className="info-row">
          <strong>Email:</strong> {customer.email}
        </div>
        <div className="info-row">
          <strong>Stock #:</strong> {truck.stockNumber}
        </div>
      </div>

      {/* Draft Mode Toggle */}
      <div className="draft-mode-section">
        <label className="draft-checkbox">
          <input 
            type="checkbox" 
            checked={saveAsDraft}
            onChange={(e) => setSaveAsDraft(e.target.checked)}
            disabled={loading}
          />
          <span className="checkbox-label">
            <strong>Save as draft</strong> - I need to add attachments before sending
          </span>
        </label>
        
        {saveAsDraft && (
          <div className="draft-info">
            <span className="info-icon">ℹ️</span>
            <span>
              Document will be created in Documenso but not sent to the customer. 
              You can add attachments, then manually click "Send" in Documenso.
            </span>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="alert alert-error">
          <strong>⚠️ Error:</strong> {error}
        </div>
      )}

      {/* Success Display */}
      {success && !signingResult && (
        <div className="alert alert-success">
          <strong>✓</strong> {success}
        </div>
      )}

      {/* Detailed Result Display */}
      {signingResult && (
        <div className="alert alert-success">
          <h3>✓ {signingResult.message}</h3>
          
          {signingResult.mode === 'draft' ? (
            // DRAFT MODE RESULT
            <div className="draft-result">
              <p>Document created as draft in Documenso.</p>
              <p className="document-id">
                <strong>Document ID:</strong> <code>{signingResult.documentId}</code>
              </p>
              
              <div className="next-steps">
                <strong>Next steps:</strong>
                <ol>
                  <li>Go to Documenso and find this document</li>
                  <li>Add any attachments you need</li>
                  <li>Click "Send" to email it to the customer</li>
                </ol>
              </div>

              <a 
                href={signingResult.documentUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                Open in Documenso →
              </a>
            </div>
          ) : (
            // SEND MODE RESULT
            <div className="send-result">
              <p>
                Signing invitation sent to: <strong>{customer.email}</strong>
              </p>
              <p className="document-id">
                <strong>Document ID:</strong> <code>{signingResult.documentId}</code>
              </p>
              
              <details className="signing-link-details">
                <summary className="signing-link-summary">
                  Show signing link (for in-person signing)
                </summary>
                <div className="signing-link-content">
                  <input 
                    type="text" 
                    value={signingResult.signingUrl} 
                    readOnly
                    className="signing-link-input"
                    onClick={(e) => e.target.select()}
                  />
                  <button
                    onClick={() => copyToClipboard(signingResult.signingUrl)}
                    className="btn btn-secondary"
                  >
                    📋 Copy Link
                  </button>
                </div>
              </details>
            </div>
          )}
        </div>
      )}

      {/* Main Action Button */}
      {!signingResult && (
        <button 
          onClick={handleSendForSignature}
          disabled={loading || !customer?.email}
          className={`btn btn-large ${saveAsDraft ? 'btn-draft' : 'btn-primary'}`}
        >
          {loading ? (
            '⏳ Processing...'
          ) : saveAsDraft ? (
            '📋 Create Draft'
          ) : (
            '✉️ Send for Signature'
          )}
        </button>
      )}

      {!loading && !signingResult && customer?.email && (
        <p className="help-text">
          Ready to send. Click the button above to proceed.
        </p>
      )}

      {!customer?.email && (
        <p className="error-text">
          ⚠️ Customer email is required
        </p>
      )}
    </div>
  );
}

export default SigningInterface;
